<template>
    <div class="form-container">
        <h2 style="margin: 0 0 16px 0; color: var(--color-ds-primary);">Run Utility</h2>
        <div class="form-section">
            <div class="form-group">
                <label>Action Name</label>
                <input type="text" placeholder="run utility" v-model="actionName" />
            </div>

            <!-- File Search Section -->
            <div class="form-group" style="padding: 10px; background-color: var(--color-ds-light-background); border-radius: var(--radius-sm);">
                <label style="font-weight: bold;">Search for Utility Files</label>
                <div style="display: flex; gap: 5px; margin-top: 8px;">
                    <input
                        type="text"
                        v-model="searchPattern"
                        placeholder=".*\.py$"
                        style="flex: 1;"
                        @keyup.enter="searchFiles"
                    />
                    <button class="button-small" @click="searchFiles" :disabled="isSearching">
                        {{ isSearching ? 'Searching...' : 'Search' }}
                    </button>
                </div>
                <div v-if="searchError" class="error-message" style="margin-top: 5px; font-size: var(--text-xs);">
                    {{ searchError }}
                </div>
                <div v-if="utilityFiles.length > 0" style="margin-top: 5px; font-size: var(--text-xs); color: var(--color-ds-light-gray);">
                    Found {{ utilityFiles.length }} file(s)
                </div>
            </div>

            <div class="form-group">
                <label>Utility Name</label>
                <select v-model="utilityName">
                    <option value="">Select Utility</option>
                    <option v-for="file in sortedUtilityFiles" :key="file.filePath" :value="'/projects' + file.filePath">
                        {{ file.filename }} ({{ file.directoryPath }})
                    </option>
                </select>
            </div>

            <div class="form-group">
                <label>Utility Params</label>
                <textarea
                    v-model="utilityParams"
                    placeholder="Enter utility parameters..."
                    rows="4"
                ></textarea>
            </div>

            <button class="button button-full" @click="addWorkflowItem">Add Workflow Item</button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { addWorkflowItem as addWorkflowItemUtil, createRunUtilityWorkflowItem } from '../utils/workflowUtils';
import { listFiles } from '../services/optilogicService';

const actionName = ref('');
const utilityName = ref('');
const utilityParams = ref('');

// File search state
const searchPattern = ref('.*\\.py$');
const utilityFiles = ref([]);
const isSearching = ref(false);
const searchError = ref('');

// Sorted utility files (alphabetically by filename)
const sortedUtilityFiles = computed(() => {
    return [...utilityFiles.value].sort((a, b) => {
        return a.filename.localeCompare(b.filename);
    });
});

// Search for files
const searchFiles = async () => {
    isSearching.value = true;
    searchError.value = '';

    try {
        // Use "Studio" as default workspace - can be made configurable later
        const files = await listFiles('Studio', searchPattern.value);
        utilityFiles.value = files;

        if (files.length === 0) {
            searchError.value = 'No files found matching the pattern';
        }
    } catch (error) {
        console.error('Error searching for files:', error);
        searchError.value = error.message || 'Failed to search for files';
        utilityFiles.value = [];
    } finally {
        isSearching.value = false;
    }
};

// Search on mount with default pattern
onMounted(() => {
    searchFiles();
});

const addWorkflowItem = async () => {
    // Remove newlines from utility params (replace with spaces)
    const cleanedParams = utilityParams.value.replace(/[\r\n]+/g, ' ').trim();

    // Create the workflow item data
    const { keys, values } = createRunUtilityWorkflowItem(
        actionName.value,
        utilityName.value,
        cleanedParams
    );

    // Add to workflow sheet using utility
    await addWorkflowItemUtil(keys, values);
};
</script>
